import { useEffect, useMemo } from "react";
import type { Paywall } from "@/react/types/Paywall";
import type { PaywallPrice } from "@/react/types/PaywallPrice";
import { pricesEligibleForTrial } from "@circle-react/helpers/paywallPriceHelpers";
import { useCreatePaywallSubscriptionFormContext } from "../CreatePaywallSubscriptionFormContextProvider";

interface PaywallPriceOption {
  label: string;
  value: number;
}

interface UsePaywallPriceFormProps {
  paywall?: Paywall;
}

interface UsePaywallPriceFormReturn {
  paywallPriceOptions: PaywallPriceOption[];
}

export const usePaywallPriceForm = ({
  paywall,
}: UsePaywallPriceFormProps): UsePaywallPriceFormReturn => {
  const [paywallPriceOptions, paywallPriceMappedById] = useMemo(() => {
    const prices = paywall?.stripe_prices_attributes;
    const paywallPriceOptions: PaywallPriceOption[] = [];
    const paywallPriceMappedById: Record<number, PaywallPrice> = {};

    if (!prices) return [[], {}];

    pricesEligibleForTrial(prices).forEach((paywallPrice: PaywallPrice) => {
      paywallPriceMappedById[paywallPrice.id] = paywallPrice;
      paywallPriceOptions.push({
        label: paywallPrice.short_description || "",
        value: paywallPrice.id,
      });
    });

    return [paywallPriceOptions, paywallPriceMappedById];
  }, [paywall]);

  const { setPaywallPricesIndex } = useCreatePaywallSubscriptionFormContext();

  useEffect(() => {
    setPaywallPricesIndex?.(paywallPriceMappedById);
  }, [paywallPriceMappedById, setPaywallPricesIndex]);

  return {
    paywallPriceOptions,
  };
};
